import { z } from 'zod';

import {
  GOOGLE_PLATFORM,
  META_PLATFORM,
  type ScheduledEnrichmentPlatform,
} from '~/features/content-enrichment/config/scheduled-enrichment-platform';
import { useFeatureGate } from '~/hooks/useFeatureGate';

/** Channel a feed audit runs for. Google is the original (and default) audit. */
export type FeedAuditPlatform = 'google' | 'meta';

/** `?platform=` on the create/upload routes; anything unknown falls back to Google. */
export const feedAuditPlatformSearchSchema = z.object({
  platform: z.enum(['google', 'meta']).catch('google'),
});

/** The trigger/connection literals for a platform — reuses the scheduled-enrichment
 *  descriptors so a Meta audit sends exactly what a Meta enrichment run sends. */
export function feedAuditPlatformConfig(platform: FeedAuditPlatform): ScheduledEnrichmentPlatform {
  return platform === 'meta' ? META_PLATFORM : GOOGLE_PLATFORM;
}

/** Whether Meta feed audits are enabled for this tenant (`feed-audit-meta` gate). */
export function useMetaFeedAuditEnabled(): boolean {
  return useFeatureGate('feed_audit_meta').isAllowed;
}

/** The platform a create/upload page should use: the requested one, except that
 *  Meta collapses to Google while the `feed-audit-meta` gate is off — so a stale or
 *  hand-typed `?platform=meta` link can never start a Meta run on a gated tenant. */
export function useEffectiveFeedAuditPlatform(requested: FeedAuditPlatform): FeedAuditPlatform {
  const metaEnabled = useMetaFeedAuditEnabled();
  return requested === 'meta' && metaEnabled ? 'meta' : 'google';
}
